import {
  daysJsFormat,
  languagesList,
  renderBeneficiary,
  renderStatusPrestation,
  renderStatusTag,
  renderTypePrestation,
  renderUserName,
  renderUserNameEmail,
  roles,
  statusControlList,
  statusPrestations,
  subRoles,
  subStatusPrestations,
  typeIntervention,
} from "../../../utils/constants";
import { ActionRoleSpecific, userHasLevel } from "../../../engine/AuthRoute";
import dayjs from "../../../utils/dayjs";
import React from "react";
import { Button, Popover, Space, Tag, theme } from "antd";
import { BiSearch } from "react-icons/bi";
import { GrPowerReset } from "react-icons/gr";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import _ from "lodash";

const before1_12_2024 = finalEndDate => {
  const now = dayjs(finalEndDate); // Créer un instantané de la date actuelle
  return now.isBefore("2024-12-01", "day"); // Comparer avec le 1er décembre 2024
};

const hourSpecial = {
  percent25: 1.25,
  percent50: 1.5,
};

const tauxHourSpecial = prestation => {
  const { finalStartDate, finalEndDate } = prestation;

  if (before1_12_2024(finalEndDate)) {
    return hourSpecial.percent25;
  } else {
    return hourSpecial.percent50;
  }
};
export const renderPrestationMajoreeTime = (
  prestation,
  showMin,
  showDetail = false,
) => {
  let copy = _.cloneDeep(prestation);
  const tranchesTime =
    copy.intervention.type === typeIntervention.in_person ? 15 : 10;

  let prestationNormal;
  let prestationSpecial;
  let isUrgent = false;

  if (copy.bill?.type === "unique") {
    prestationNormal = copy.bill.costs?.find(e => e.type === "prestation");
    prestationSpecial = copy.bill.costs?.find(e => e.type === "night");
    isUrgent = copy.bill.costParams?.isUrgent;
  } else if (copy.bill?.type === "multiple") {
    const prestationData = copy.bill.costsMultiple?.find(
      e => e.prestationId === copy._id,
    );
    prestationNormal = prestationData?.cost?.find(e => e.type === "prestation");
    prestationSpecial = prestationData?.cost?.find(e => e.type === "night");
    isUrgent = prestationData?.costParams?.isUrgent;
  }

  const multiplierIsUrgent = isUrgent ? 1.5 : 1;
  const tauxSpecial = tauxHourSpecial(copy);

  // On doit soustrtaire les heures spécial car les h normal calcule toute la durée pour le prix car c'êst une majoration mais niveau temps non
  if (prestationSpecial) {
    prestationNormal.tranches -= prestationSpecial.tranches;
  }

  const normalTime = prestationNormal
    ? prestationNormal.tranches * tranchesTime * multiplierIsUrgent
    : 0;

  const specialTime = prestationSpecial
    ? prestationSpecial.tranches *
      tranchesTime *
      multiplierIsUrgent *
      tauxSpecial
    : 0;

  const totalTime = normalTime + specialTime;

  if (showDetail) {
    const normalDetail = prestationNormal
      ? `${prestationNormal.tranches} tranches * ${tranchesTime} minutes ${
          isUrgent ? "* urgent (x1.5)" : ""
        }`
      : "Pas de prestation normale";

    const specialDetail = prestationSpecial
      ? `${prestationSpecial.tranches} tranches * ${tranchesTime} minutes ${
          isUrgent ? "* urgent (x1.5)" : ""
        } * taux spécial (${tauxSpecial})`
      : "Pas d'heure spéciale";

    return (
      <>
        <ul>
          <li>
            Temps normal : {normalDetail} = <b>{normalTime} min</b>
          </li>
          <li>
            Temps spécial : {specialDetail} = <b>{specialTime} min</b>
          </li>
          <li>
            <b>
              Total : {totalTime} {showMin ? "min" : ""}
            </b>
          </li>
        </ul>
      </>
    );
  }

  // Retourner la somme des deux temps en minutes
  return `${totalTime} ${showMin ? "min" : ""}`;
};

export const columnsPrestationsList = (sortedPrestations, searchState) => {
  const uniqueValuesSetCategory = new Set();

  const { advancedCols = false } = searchState;

  sortedPrestations.map(obj => {
    obj?.category?.map(cat => {
      uniqueValuesSetCategory.add(cat);
    });
    uniqueValuesSetCategory.add(obj.type);
  });

  const uniqueSubStatus = new Set();

  sortedPrestations.map(obj => {
    if (obj?.subStatus) {
      uniqueSubStatus.add(obj.subStatus);
    }
  });

  let arrayCols = [
    {
      title: "N°",
      width: 100,
      dataIndex: "reference",
      key: "reference",
      sorter: (a, b) => {
        return a?.reference - b?.reference;
      },
    },
    {
      title: "Interprète",
      dataIndex: "interprete",
      key: "interprete",
      sorter: (a, b) => {
        const nameA = renderUserName(a.interprete);
        const nameB = renderUserName(b.interprete);

        return nameA.localeCompare(nameB);
      },
      visible: userHasLevel({
        not: {
          [roles.interprete]: "r", //
        },
      }),
      render: (interprete, record) => {
        return <b>{renderUserName(interprete)}</b>;
      },
    },
    {
      title: "Institution",
      visible: userHasLevel({
        not: {
          [roles.institution]: "r", //
        },
      }),
      dataIndex: "institution",
      key: "institution",
      sorter: (a, b) => a.institution?.name.localeCompare(b.institution?.name),
      render: institution => {
        return institution?.name;
      },
    },
    {
      title: "Référence bénéficiaire",
      sm: false,
      dataIndex: "beneficiary",
      key: "beneficiary",
      sorter: (a, b) =>
        a.beneficiary.reference.localeCompare(b.beneficiary.reference),
      render: beneficiary => {
        return renderBeneficiary(beneficiary);
      },
    },
    {
      title: "Lieu d'intervention",
      dataIndex: "intervention.location",
      key: "intervention.location",
      render: (intervention, record) => {
        return record.intervention.location;
      },
    },
    {
      title: "Demandeur",
      dataIndex: "applicant",
      key: "applicant",
      render: (type, item) => {
        return renderUserName(item.applicant);
      },
      sorter: (a, b) => {
        return a?.applicant?._id?.localeCompare(b?.applicant?._id);
      },
    },
    {
      title: "Début",
      dataIndex: "finalStartDate",
      key: "finalStartDate",
      render: (n, record) => {
        let dateToShow;
        if (record?.interventionFinal?.startDate !== null) {
          dateToShow = record?.interventionFinal?.startDate;
        } else {
          dateToShow = record?.intervention?.startDate;
        }
        return <>{dayjs(dateToShow).format(daysJsFormat.full)}</>;
      },
      sorter: (a, b) => {
        let dateA =
          a.interventionFinal.startDate !== null
            ? a.interventionFinal.startDate
            : a.intervention.startDate;
        let dateB =
          b.interventionFinal.startDate !== null
            ? b.interventionFinal.startDate
            : b.intervention.startDate;
        return new Date(dateA) - new Date(dateB);
      },
    },
    {
      title: "Fin",
      dataIndex: "finalEndDate",
      key: "finalEndDate",
      render: (n, record) => {
        let dateToShow;
        if (record?.interventionFinal?.endDate !== null) {
          dateToShow = record?.interventionFinal?.endDate;
        } else {
          dateToShow = record?.intervention?.endDate;
        }
        return <>{dayjs(dateToShow).format(daysJsFormat.full)}</>;
      },
      sorter: (a, b) => {
        let dateA =
          a.interventionFinal.endDate !== null
            ? a.interventionFinal.endDate
            : a.intervention.endDate;
        let dateB =
          b.interventionFinal.endDate !== null
            ? b.interventionFinal.endDate
            : b.intervention.endDate;
        return new Date(dateA) - new Date(dateB);
      },
    },
    {
      title: "Etat de la commande",
      sm: false,
      dataIndex: "status",
      key: "status",
      width: "200px",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status, record) => {
        return (
          <>
            <ActionRoleSpecific only={[roles.admin, roles.permanance]}>
              {renderStatusPrestation(record, { showControls: true })}
            </ActionRoleSpecific>

            <ActionRoleSpecific only={[roles.interprete, roles.institution]}>
              {renderStatusPrestation(record, { showControls: false })}
            </ActionRoleSpecific>
          </>
        );
      },
    },
  ];

  if (advancedCols === true) {
    arrayCols.push({
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (n, record) => {
        return <>{renderTypePrestation(record.intervention.type)}</>;
      },
    });

    arrayCols.push({
      title: "Début facturé",
      dataIndex: "intervention.startDate",
      key: "intervention.startDate",
      render: (n, record) => (
        <>{dayjs(record.finalStartDate).format(daysJsFormat.full)}</>
      ),
      sorter: (a, b) => new Date(a.finalStartDate) - new Date(b.finalStartDate),
    });

    arrayCols.push({
      title: "Fin facturé",
      dataIndex: "intervention.endDate",
      key: "intervention.endDate",
      render: (n, record) => (
        <>{dayjs(record.finalEndDate).format(daysJsFormat.full)}</>
      ),
      sorter: (a, b) => new Date(a.finalEndDate) - new Date(b.finalEndDate),
    });

    arrayCols.push({
      title: "Durée effective (majorée)",
      dataIndex: "bill",
      key: "bill",
      width: 120,
      render: (n, record) => {
        return (
          <span>
            {record?.bill === undefined || record?.bill === null ? (
              <Tag color={"grey"}>Non facturé</Tag>
            ) : null}

            <Popover
              placement="topLeft"
              title={"Détail du calcul des heure effective"}
              content={
                <span>{renderPrestationMajoreeTime(record, true, true)}</span>
              }
            >
              {renderPrestationMajoreeTime(record, true)}
            </Popover>
          </span>
        );
      },
    });
    arrayCols.push({
      title: "Horaires spéciaux",
      dataIndex: "bill",
      key: "bill",
      render: (n, record) => {
        let isUrgent = false;
        let isHorsCanton = false;

        if (record.bill?.type === "unique") {
          isUrgent = record?.bill?.costParams.isUrgent;
          isHorsCanton = record?.bill?.costParams.isHorsCanton;
        }
        if (record.bill?.type === "multiple") {
          isUrgent = record?.bill?.costsMultiple?.find(
            e => e.prestationId === record._id,
          )?.costParams.isUrgent;
          isHorsCanton = record?.bill?.costsMultiple?.find(
            e => e.prestationId === record._id,
          )?.costParams.isHorsCanton;
        }

        return (
          <span>
            {isUrgent && <Tag color={"red-inverse"}>Urgent</Tag>}
            {isHorsCanton && <Tag color={"blue-inverse"}>Hors canton</Tag>}
          </span>
        );
      },
    });

    arrayCols.push({
      title: "Kms facturés",
      dataIndex: "bill",
      key: "bill",
      render: (n, record) => {
        let travel = undefined;

        if (record.bill?.type === "unique") {
          travel = record?.bill?.costs?.find(e => e.type === "travel");
        }
        if (record.bill?.type === "multiple") {
          let findCost = record?.bill?.costsMultiple?.find(
            e => e.prestationId === record._id,
          );
          travel = findCost?.cost?.find(e => e.type === "travel");
        }

        return <>{travel !== undefined ? <>{travel.tranches} kms</> : 0} </>;
      },
    });
  }

  return arrayCols;
};

export const inputsPrestationsList = (
  defaultRanges,
  formRef,
  setSearchState,
  setPagination,
  dispatchActionPrestations,
  defaultPagination,
  loading,
  searchState,
  advancedSearch,
  setAdvancedSearch,
) => {
  const {
    token: { colorPrimary },
  } = theme.useToken();

  return [
    {
      parentLabel: null,
      extraRowStyle: { alignItems: "center" },
      children: [
        {
          visible: userHasLevel({
            not: {
              [roles.interprete]: "r", //
            },
          }),
          label: "Filtre par interprète",
          name: "interprete",
          placeholder: "Filtre par interprète",
          allowClear: true,
          showSearch: true,
          value: searchState?.interprete || undefined,
          type: "dynamic-select",
          endPoint: "users?role=interprete",
          capitalize: false,
          itemCallback: item => {
            return {
              key: item._id,
              label: renderUserName(item),
              value: item._id,
            };
          },
          xl: 6,
          lg: 5,
          md: 6,
          sm: 8,
          xs: 12,
        },
        {
          visible: userHasLevel({
            not: {
              [roles.institution]: "r", //
            },
          }),
          label: "Filtre par institution",
          name: "institution",
          placeholder: "Filtre par institution",
          allowClear: true,
          showSearch: true,
          value: searchState?.institution || undefined,
          type: "dynamic-select",
          endPoint: "institutions",
          itemCallback: item => {
            return {
              key: item._id,
              label: `${item.name}`,
              value: item._id,
            };
          },
          capitalize: false,
          xl: 6,
          lg: 5,
          md: 6,
          sm: 8,
          xs: 12,
        },

        {
          label: "Filtre par n° de commande",
          name: "reference",
          value: searchState?.reference || null,
          type: "input",
          xl: advancedSearch ? 6 : 5,
          lg: advancedSearch ? 5 : 4,
          md: 6,
          sm: 8,
          xs: 12,
        },
        {
          visible: advancedSearch,
          label: "Filtre par date",
          name: "rangeDate",
          value: searchState?.rangeDate
            ? [
                dayjs(searchState?.rangeDate[0]),
                dayjs(searchState?.rangeDate[1]),
              ]
            : defaultRanges,
          type: "range-picker",
          xl: 6,
          lg: 6,
          md: 6,
          sm: 8,
          xs: 12,
        },
        {
          visible: advancedSearch,
          label: "Filtre par bénéficiaire",
          name: "beneficiary",
          value: searchState?.beneficiary || null,
          type: "input",
          xl: 6,
          lg: 5,
          md: 6,
          sm: 8,
          xs: 12,
        },
        {
          visible:
            advancedSearch &&
            userHasLevel({
              not: {
                [roles.institution]: "r", //
                [roles.interprete]: "r", //
              },
            }),
          label: "Filtre par demandeur",
          name: "institutionDemander",
          placeholder: "Filtre par demandeur",
          allowClear: true,
          showSearch: true,
          value: searchState?.institutionDemander || undefined,
          type: "dynamic-select",
          endPoint: `users?subRole=${subRoles.institutionDemander}&dynamic=true&status=active,inactive`,
          itemCallback: item => {
            return {
              key: item._id,
              label: renderUserName(item),
              value: item._id,
            };
          },
          capitalize: false,
          xl: 6,
          lg: 5,
          md: 6,
          sm: 8,
          xs: 12,
        },
        {
          visible: advancedSearch,
          label: "Filtre par langue",
          name: "language",
          type: "dynamic-select",
          endPoint: "users/languages",
          value:
            searchState?.language !== null &&
            searchState?.language !== undefined
              ? languagesList.find(e => e.value === searchState?.language).label
              : searchState?.language || [],
          showSearch: true,
          allowClear: true,
          reset: true,
          multiple: false,
          itemCallback: item => {
            return {
              key: item.value,
              label: `${
                languagesList.find(e => e.value === item.value)?.label
              }`,
              value: item.value,
            };
          },
          xl: 6,
          lg: 5,
          md: 6,
          sm: 8,
          xs: 12,
        },
        {
          label: "Filtre par contrôle",
          visible:
            advancedSearch &&
            userHasLevel({
              only: {
                [roles.admin]: "r", //
                [roles.permanance]: "r", //
              },
            }),
          name: "control",
          type: "select",
          value: searchState?.control || [],
          showSearch: true,
          allowClear: true,
          reset: true,
          multiple: true,
          selectContent: [
            ...statusControlList.map(item => {
              return {
                ...item,
                label: `1er ${item.label}`,
                value: `${item.value}_1`,
              };
            }),
            ...statusControlList.map(item => {
              return {
                ...item,
                label: `2ème ${item.label}`,
                value: `${item.value}_2`,
              };
            }),
          ],
          xl: 6,
          lg: 5,
          md: 6,
          sm: 8,
          xs: 12,
        },
        {
          label: "Filtre par auteur",
          visible:
            advancedSearch &&
            userHasLevel({
              only: {
                [roles.admin]: "r", //
                [roles.permanance]: "r", //
              },
            }),
          name: "creatorRole",
          type: "select",
          value: searchState?.creatorRole || "all",
          showSearch: true,
          selectContent: [
            { label: "Tous les utilisateurs", value: "all" },
            { label: "Admin / Permanence", value: "admin" },
            { label: "Institution", value: "institution" },
          ],
          xl: 6,
          lg: 5,
          md: 6,
          sm: 8,
          xs: 12,
        },
        {
          label: "Informations avancées",
          visible: advancedSearch,
          name: "advancedCols",
          type: "switch",
          value: searchState?.advancedCols || false,
          xl: 6,
          lg: 5,
          md: 6,
          sm: 8,
          xs: 12,
        },
        // {
        //   label: "Filtre par batch",
        //   visible:
        //     advancedSearch &&
        //     userHasLevel({
        //       only: {
        //         [roles.admin]: "r", //
        //         [roles.permanance]: "r", //
        //       },
        //     }),
        //   name: "batch",
        //   type: "select",
        //   value: searchState?.batch || "all",
        //   showSearch: true,
        //   allowClear: true,
        //   reset: true,
        //   selectContent: [
        //     {
        //       label: `Tous`,
        //       value: `all`,
        //     },
        //     {
        //       label: `Partagé`,
        //       value: `batch_shared`,
        //     },
        //     {
        //       label: `Groupé`,
        //       value: `batch_grouped`,
        //     },
        //   ],
        //   xl: 6,
        //   lg: 5,
        //   md: 6,
        //   sm: 8,
        //   xs: 12,
        // },
        {
          type: "component",
          style: { textAlign: "left" },
          xl: advancedSearch ? 24 : 7,
          lg: 7,
          md: 7,
          sm: 12,
          xs: 24,
          label: "",
          component: () => (
            <Space
              align={"center"}
              style={{ width: "100%", justifyContent: "flex-end" }}
            >
              <Button
                loading={loading}
                size={"middle"}
                type={"default"}
                style={{
                  marginTop: "-6px",
                  width: "100%",
                  fontWeight: "bold",
                }}
                icon={<BiSearch style={{ marginTop: "0px" }} size={25} />}
                onClick={() => formRef.current.submit()}
              >
                Rechercher
              </Button>
              <Button
                style={{
                  marginTop: "-7px",
                  width: "100%",
                  color: colorPrimary,
                  fontWeight: "bold",
                }}
                icon={<GrPowerReset style={{ marginTop: "-3px" }} size={15} />}
                onClick={async () => {
                  localStorage.removeItem("prestationsSearcher");

                  setSearchState({});

                  formRef.current.resetFields(); // Reset the form fields using the ref

                  formRef.current.setFieldsValue({
                    interprete: undefined,
                    institution: undefined,
                    institutionDemander: undefined,
                    rangeDate: defaultRanges,
                    reference: null,
                    beneficiary: null,
                    language: [],
                    control: [],
                  });
                  // formRef.current.setFieldsValue(initialValuesForm);
                  setPagination(defaultPagination);
                  await dispatchActionPrestations();
                }}
                size="middle"
                type={"text"}
              >
                Réinitialiser
              </Button>
              <Button
                style={{
                  marginTop: "-7px",
                  width: "100%",
                  color: colorPrimary,
                  fontWeight: "bold",
                }}
                icon={
                  advancedSearch ? (
                    <FaChevronUp style={{ marginTop: "-3px" }} size={15} />
                  ) : (
                    <FaChevronDown style={{ marginTop: "-3px" }} size={15} />
                  )
                }
                onClick={async () => {
                  setAdvancedSearch(!advancedSearch);
                }}
                size="middle"
                type={"text"}
              >
                {advancedSearch ? "Moins de filtres" : "Plus de filtres"}
              </Button>
            </Space>
          ),
        },
      ],
    },
  ];
};

export const defaultSorterByTab = tab => {
  switch (tab) {
    case "draft":
    case "schedules":
    case "min72h":
    case "today":
    case "toConfirm":
      return {
        key: "finalStartDate",
        order: "ascend",
      };
    case "total":
    case "done":
    case "cancelled":
    case "nonClosed":
      return {
        key: "finalStartDate",
        order: "descend",
      };
    default:
      return {
        key: "finalStartDate",
        order: "ascend",
      };
  }
};
